import { useState, useEffect, useRef, type ComponentProps } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  Video, 
  Stethoscope,
  Users, 
  BarChart3,
  UserCheck,
  AlertCircle,
  Copy,
  Bell,
  Volume2,
  VolumeX
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { AvailabilityService } from '@/lib/services/availabilityService';
import { PresenceService, WaitingCustomer } from '@/lib/services/presenceService';
import { 
  CoachAvailabilitySession, 
  WaitingRoom, 
  CoachAvailabilityStatus
} from '@/types/availability';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { format, formatDistanceToNow } from 'date-fns';
import { FloatingVideoCall } from '@/components/video/FloatingVideoCall';
import { NotesDialog } from './NotesDialog';
import { AttendedCustomersCard } from './AttendedCustomersCard';
import { doc } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { detectNewArrivals } from '@/lib/notifications/waitingRoomArrivals';
import { useWaitingRoomAlerts } from '@/hooks/use-waiting-room-alerts';
import { shouldRecordAttendance, type CallOutcome } from '@/lib/clinic/callSession';

export function CoachAvailabilityDashboard() {
  const { t, language } = useLanguage();
  const { currentUser, userData } = useAuth();
  const [availabilitySession, setAvailabilitySession] = useState<CoachAvailabilitySession | null>(null);
  const [waitingRoom, setWaitingRoom] = useState<WaitingRoom | null>(null);
  const [presenceCustomers, setPresenceCustomers] = useState<WaitingCustomer[]>([]); // Customers with active heartbeat
  const [isLoading, setIsLoading] = useState(true);
  const [showVideoCall, setShowVideoCall] = useState(false);
  const [currentCallRoomId, setCurrentCallRoomId] = useState<string>('');
  const [currentCustomerName, setCurrentCustomerName] = useState<string>('');
  const [currentCallCustomerId, setCurrentCallCustomerId] = useState<string>('');
  const inCallRef = useRef(false);
  inCallRef.current = showVideoCall;
  const [showCustomerFile, setShowCustomerFile] = useState(false);
  const [selectedCustomerForFile, setSelectedCustomerForFile] = useState<ComponentProps<typeof NotesDialog>['appointment'] | null>(null);
  
  // Settings state (loaded from global admin settings)
  const [maxQueueSize, setMaxQueueSize] = useState(5);
  const [averageCallDuration, setAverageCallDuration] = useState(15);
  const [breakBetweenCalls, setBreakBetweenCalls] = useState(2);
  const [hasApprovedAvailability, setHasApprovedAvailability] = useState<boolean | null>(null);
  const [allowOpenOutsideSchedule, setAllowOpenOutsideSchedule] = useState(false);
  const [showStatistics, setShowStatistics] = useState(true);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  // #48: chime / toast / title flash / desktop notification on new arrivals
  const waitingRoomAlerts = useWaitingRoomAlerts({
    clinicOpen: availabilitySession?.status === 'open',
    language,
  });
  const { notifyArrivals } = waitingRoomAlerts;

  useEffect(() => {
    if (!currentUser) return;
    
    loadAvailabilityData();
    
    // Listen to waiting room updates from Firestore (for active call, stats, etc.)
    const unsubscribeWaitingRoom = AvailabilityService.listenToWaitingRoom(
      currentUser.uid,
      async (room) => {
        // Auto-clear stale active calls (older than 30 minutes)
        if (room?.activeCall?.startTime) {
          const callStartTime = room.activeCall.startTime.toDate?.();
          // Never while this dashboard is itself in that call (a long real call is not stale)
          if (callStartTime && !inCallRef.current && (Date.now() - callStartTime.getTime()) > 30 * 60 * 1000) {
            console.log('[CoachDashboard] Clearing stale active call');
            // Not an attendance: a stale call is not a 0-minute visit (#47)
            await AvailabilityService.cancelActiveCall(currentUser.uid, room.activeCall.roomId);
            return;
          }
        }
        
        setWaitingRoom(room);
        // The server closes the clinic 30 minutes after its scheduled end
        // (#45, autoCloseClinics). The session here is a one-shot read, so
        // follow the room's coachStatus — the same close writes both.
        if (room?.coachStatus === 'closed') {
          setAvailabilitySession((session) =>
            session && session.status !== 'closed' ? { ...session, status: 'closed' } : session
          );
        }
        setIsLoading(false);
      }
    );

    // Listen to customers with active heartbeat (filters out stale).
    // `seenArrivals` is per subscription: the first snapshot is the baseline
    // (no alert for whoever is already waiting), later ones alert only for
    // genuinely new joins — not heartbeat re-writes (#48).
    let seenArrivals: Set<string> | null = null;
    const unsubscribePresence = PresenceService.listenToWaitingRoom(
      currentUser.uid,
      (customers) => {
        setPresenceCustomers(customers);
        const { arrivals, seen } = detectNewArrivals(seenArrivals, customers);
        seenArrivals = seen;
        notifyArrivals(arrivals);
      }
    );
    
    // Periodically cleanup stale entries from Firestore
    const cleanupInterval = setInterval(() => {
      PresenceService.cleanupStaleEntries(currentUser.uid).catch(() => {});
    }, 10000); // Every 10 seconds
    
    return () => {
      unsubscribeWaitingRoom();
      unsubscribePresence();
      clearInterval(cleanupInterval);
    };
  }, [currentUser, notifyArrivals]);

  // Monitor and close clinic only when the day has passed
  useEffect(() => {
    // Don't run until settings are loaded from Firestore
    if (!settingsLoaded) {
      return;
    }

    const checkAndCloseIfDayPassed = async () => {
      if (!currentUser || !availabilitySession || availabilitySession.status === 'closed') {
        return;
      }

      // Check if the session is from a previous day
      const today = format(new Date(), 'yyyy-MM-dd');
      const sessionDate = availabilitySession.date;
      
      if (sessionDate && sessionDate !== today) {
        // Day has passed, close the clinic
        await AvailabilityService.setCoachAvailability(currentUser.uid, 'closed', {
          maxQueueSize,
          averageCallDuration,
          breakBetweenCalls,
          autoBreakAfterCalls: 4,
        });
        toast.info(language === 'ar' 
          ? 'تم إغلاق العيادة تلقائياً - بداية يوم جديد'
          : 'Clinic automatically closed - new day started'
        );
      }
    };

    // Check every minute
    const interval = setInterval(checkAndCloseIfDayPassed, 60000);
    
    // Also check immediately
    checkAndCloseIfDayPassed();

    return () => clearInterval(interval);
  }, [currentUser, availabilitySession, settingsLoaded, maxQueueSize, averageCallDuration, breakBetweenCalls, language]);


  const loadAvailabilityData = async () => {
    if (!currentUser) return;
    
    try {
      const session = await AvailabilityService.getCoachAvailability(currentUser.uid);
      setAvailabilitySession(session);
      
      // Load global clinic settings from admin config
      await loadClinicSettings();

      // Check if coach has approved availability sessions
      await checkApprovedAvailability();
    } catch (error) {
      console.error('Error loading availability data:', error);
    }
  };

  const loadClinicSettings = async () => {
    try {
      const { getDoc } = await import('firebase/firestore');
      const clinicSettingsDoc = await getDoc(doc(db, 'settings', 'clinicSettings'));
      
      if (clinicSettingsDoc.exists()) {
        const data = clinicSettingsDoc.data();
        setMaxQueueSize(data.maxQueueSize || 5);
        setAverageCallDuration(data.averageCallDuration || 15);
        setBreakBetweenCalls(data.breakBetweenCalls || 2);
        setAllowOpenOutsideSchedule(data.allowOpenOutsideSchedule || false);
        setShowStatistics(data.showStatistics !== false); // Default to true
      }
      setSettingsLoaded(true);
    } catch (error) {
      console.error('Error loading clinic settings:', error);
      setSettingsLoaded(true); // Still mark as loaded to prevent infinite waiting
    }
  };

  const checkApprovedAvailability = async () => {
    if (!currentUser) return;
    
    try {
      const { collection, query, where, getDocs } = await import('firebase/firestore');
      const { db } = await import('@/lib/firebase/config');
      
      const availabilitiesQuery = query(
        collection(db, 'availabilities'),
        where('coachId', '==', currentUser.uid),
        where('status', '==', 'approved')
      );
      
      const availabilitiesSnapshot = await getDocs(availabilitiesQuery);
      setHasApprovedAvailability(!availabilitiesSnapshot.empty);
    } catch (error) {
      console.error('Error checking approved availability:', error);
      setHasApprovedAvailability(false);
    }
  };

  const checkCurrentTimeAgainstSchedule = async (): Promise<boolean> => {
    if (!currentUser?.uid) return false;
    
    try {
      const { collection, query, where, getDocs } = await import('firebase/firestore');
      const { db } = await import('@/lib/firebase/config');
      
      // Get approved availabilities for current coach
      const availabilitiesQuery = query(
        collection(db, 'availabilities'),
        where('coachId', '==', currentUser.uid),
        where('status', '==', 'approved')
      );
      
      const availabilitiesSnapshot = await getDocs(availabilitiesQuery);
      
      if (availabilitiesSnapshot.empty) {
        return false; // No approved availability sessions
      }
      
      const now = new Date();
      const currentDay = now.getDay().toString(); // 0 = Sunday, 1 = Monday, etc.
      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();
      const currentTime = currentHour * 60 + currentMinute; // Convert to minutes
      
      // Check if current time falls within any approved availability
      for (const doc of availabilitiesSnapshot.docs) {
        const availability = doc.data();
        
        // Check if availability is active (not expired)
        if (availability.endDate && availability.endDate.toDate() < now) {
          continue; // Skip expired availability
        }
        
        if (availability.startDate && availability.startDate.toDate() > now) {
          continue; // Skip future availability
        }
        
        // Check if current day is in selected days
        const selectedDays = availability.selectedDays || {};
        const daySessions = selectedDays[currentDay] || [];
        
        if (daySessions.length === 0) {
          continue; // No sessions for current day
        }
        
        // Check if current time falls within any session
        for (const session of daySessions) {
          let sessionStart, sessionEnd;
          
          if (session === 'session1') {
            sessionStart = 17 * 60; // 5:00 PM
            sessionEnd = 20 * 60;   // 8:00 PM
          } else if (session === 'session2') {
            sessionStart = 20 * 60; // 8:00 PM
            sessionEnd = 22 * 60;   // 10:00 PM
          } else {
            continue; // Unknown session
          }
          
          if (currentTime >= sessionStart && currentTime < sessionEnd) {
            return true; // Current time is within approved schedule
          }
        }
      }
      
      return false; // Current time is not within any approved schedule
    } catch (error) {
      console.error('Error checking schedule:', error);
      return false;
    }
  };

  const handleStatusChange = async (status: CoachAvailabilityStatus) => {
    if (!currentUser) return;
    
    // Check if trying to set "open" status
    if (status === 'open') {
      // Only enforce schedule restrictions if admin hasn't allowed opening outside schedule
      if (!allowOpenOutsideSchedule) {
        // Check if coach has approved availability
        if (hasApprovedAvailability === false) {
          toast.error(language === 'ar' 
            ? 'لا يمكن فتح العيادة بدون جداول معتمدة'
            : 'Cannot open clinic without approved schedules'
          );
          return;
        }
        
        // Check if within approved hours
        const isWithinSchedule = await checkCurrentTimeAgainstSchedule();
        if (!isWithinSchedule) {
          toast.error(language === 'ar' 
            ? 'لا يمكن فتح العيادة خارج ساعات التوفر المعتمدة (5:00 PM - 10:00 PM)'
            : 'Cannot open clinic outside approved availability hours (5:00 PM - 10:00 PM)'
          );
          return;
        }
      }
    }
    
    try {
      // Update Firestore
      await AvailabilityService.setCoachAvailability(currentUser.uid, status, {
        maxQueueSize,
        averageCallDuration,
        breakBetweenCalls,
        autoBreakAfterCalls: 4
      });
      
      // Also update RTDB clinic status for presence service
      await PresenceService.setClinicStatus(currentUser.uid, status);
      
      await loadAvailabilityData();
      toast.success(
        language === 'ar' 
          ? 'تم تحديث حالة التوفر بنجاح'
          : 'Availability status updated successfully'
      );
    } catch (error) {
      console.error('Error updating availability:', error);
      toast.error(
        language === 'ar' 
          ? 'فشل في تحديث حالة التوفر'
          : 'Failed to update availability status'
      );
    }
  };

  const handleStartCall = async (customerId: string) => {
    console.log('[CoachDashboard] handleStartCall called with customerId:', customerId);
    console.log('[CoachDashboard] currentUser:', currentUser?.uid);
    console.log('[CoachDashboard] presenceCustomers:', presenceCustomers);
    
    if (!currentUser || !customerId) {
      console.log('[CoachDashboard] Early return: no currentUser or customerId');
      return;
    }
    
    try {
      // Find the customer from presence customers
      const customer = presenceCustomers.find(c => c.customerId === customerId);
      console.log('[CoachDashboard] Found customer:', customer);
      if (!customer) {
        toast.error(language === 'ar' ? 'المستفيد غير موجود' : 'Customer not found');
        return;
      }

      // Generate room ID
      const roomId = `availability-${currentUser.uid}-${customerId}-${Date.now()}`;

      // The customer's presence doc is removed only once they actually join
      // the call (handleRemoteJoined) — if this attempt fails they must still
      // be in the waiting room (#47).
      console.log('[CoachDashboard] Setting active call in Firestore...');
      // Set active call in Firestore (for customer to detect)
      await AvailabilityService.setActiveCall(currentUser.uid, {
        customerId: customer.customerId,
        customerName: customer.customerName,
        customerPhone: customer.customerPhone,
        roomId,
        // Which presence session is called (#47): lets the beneficiary accept by identity
        joinTime: customer.joinTime ?? null
      });
      
      // Create appointment object for customer file
      const customerAppointment = {
        id: `waiting-${customer.customerId}`,
        customerId: customer.customerId,
        customerName: customer.customerName,
        customerPhone: customer.customerPhone,
        date: new Date(),
        coachId: currentUser.uid,
        status: 'scheduled' as const,
        notes: ''
      };
      
      // Set customer file data
      setSelectedCustomerForFile(customerAppointment);
      setShowCustomerFile(true);
      
      console.log('[CoachDashboard] Starting video call with roomId:', roomId);
      // Start the video call
      setCurrentCallRoomId(roomId);
      setCurrentCustomerName(customer.customerName);
      setCurrentCallCustomerId(customer.customerId);
      setShowVideoCall(true);
      
      console.log('[CoachDashboard] Video call started successfully');
      toast.success(
        language === 'ar' 
          ? `بدء المكالمة مع ${customer.customerName}`
          : `Starting call with ${customer.customerName}`
      );
    } catch (error) {
      console.error('Error starting call:', error);
      toast.error(
        language === 'ar' 
          ? 'فشل في بدء المكالمة'
          : 'Failed to start call'
      );
    }
  };


  // The customer joined the LiveKit room: now they leave the waiting room.
  const handleRemoteJoined = () => {
    if (!currentUser || !currentCallCustomerId) return;
    PresenceService.removeFromWaitingRoom(currentUser.uid, currentCallCustomerId).catch((error) => {
      console.error('[CoachDashboard] Error removing customer from waiting room:', error);
    });
  };

  const handleCallEnded = async (durationMinutes: number, outcome?: CallOutcome) => {
    if (!currentUser) return;

    try {
      if (shouldRecordAttendance(outcome)) {
        // End the current call in the availability service with actual duration
        await AvailabilityService.endCurrentCall(currentUser.uid, durationMinutes);
      } else {
        // Never connected: no attendance, no stats; the customer stays in the
        // waiting room (their presence doc was not removed) (#47)
        await AvailabilityService.cancelActiveCall(currentUser.uid, currentCallRoomId || undefined);
      }

      // Close the video call and reset states
      setShowVideoCall(false);
      setCurrentCallRoomId('');
      setCurrentCustomerName('');
      setCurrentCallCustomerId('');

      if (shouldRecordAttendance(outcome)) {
        toast.success(
          language === 'ar'
            ? 'انتهت المكالمة'
            : 'Call ended'
        );
      } else {
        toast.info(
          language === 'ar'
            ? 'لم يكتمل الاتصال، لم يُسجَّل حضور المستفيد'
            : 'The call did not connect; attendance was not recorded'
        );
      }
    } catch (error) {
      console.error('Error ending call:', error);
      toast.error(
        language === 'ar' 
          ? 'فشل في إنهاء المكالمة'
          : 'Failed to end call'
      );
    }
  };



  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentStatus = availabilitySession?.status || 'closed';
  // Use presence customers (filtered by heartbeat) as source of truth
  const customersCount = presenceCustomers.length;
  
  // Check if active call is stale (older than 30 minutes)
  const getActiveCallAge = (): number | null => {
    const activeCall = waitingRoom?.activeCall;
    if (!activeCall?.startTime) return null;
    
    let startTime: Date;
    if (typeof activeCall.startTime.toDate === 'function') {
      startTime = activeCall.startTime.toDate();
    } else if (activeCall.startTime instanceof Date) {
      startTime = activeCall.startTime;
    } else if (typeof activeCall.startTime === 'number') {
      startTime = new Date(activeCall.startTime);
    } else {
      // Unknown format - treat as stale
      return Infinity;
    }
    
    return (Date.now() - startTime.getTime()) / (1000 * 60); // Age in minutes
  };
  
  const activeCallAgeMinutes = getActiveCallAge();
  // Reduce stale threshold to 5 minutes - calls shouldn't last that long without activity
  const isActiveCallStale = activeCallAgeMinutes !== null && activeCallAgeMinutes > 5;
  
  // Only consider it an active call if it's not stale
  const hasActiveCall = !!waitingRoom?.activeCall && !isActiveCallStale;
  

  // Generate shareable clinic link when clinic is open or on break
  const getShareableLink = () => {
    if (!currentUser?.uid) return '';
    const baseUrl = window.location.origin;
    return `${baseUrl}/clinic/${currentUser.uid}`;
  };

  const handleCopyLink = () => {
    const link = getShareableLink();
    navigator.clipboard.writeText(link);
    toast.success(
      language === 'ar' 
        ? 'تم نسخ الرابط!' 
        : 'Link copied!'
    );
  };

  // Show shareable link when clinic is open
  const showShareableLink = currentStatus === 'open';

  return (
    <div className="space-y-4 md:space-y-6">
      
      {/* Status Control */}
      <Card>
        <CardHeader className="p-3 md:p-4 lg:p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="flex items-center gap-2 text-base md:text-lg">
              <Stethoscope className="h-4 w-4 md:h-5 md:w-5 flex-shrink-0" />
              {t('availability.dashboard.title')}
            </CardTitle>
            
            <div className="flex items-center gap-3 md:gap-4">
              {/* Status Label */}
              <span className={`text-sm font-semibold ${
                currentStatus === 'open' ? 'text-[#7dda55]' : 'text-red-500'
              }`}>
                {currentStatus === 'open' 
                  ? (language === 'ar' ? 'مفتوحة' : 'Open')
                  : (language === 'ar' ? 'مغلقة' : 'Closed')
                }
              </span>
              
              {/* Toggle Switch */}
              <button
                dir="ltr"
                onClick={() => handleStatusChange(currentStatus === 'open' ? 'closed' : 'open')}
                disabled={hasActiveCall}
                className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#7dda55] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                  currentStatus === 'open' ? 'bg-[#7dda55]' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${
                    currentStatus === 'open' ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-3 md:space-y-4 p-3 md:p-4 lg:p-6 pt-0">
          {/* Stale Call Warning */}
          {isActiveCallStale && waitingRoom?.activeCall && (
            <div className="p-3 md:p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <div className="flex items-start gap-2 md:gap-3">
                <AlertCircle className="h-4 w-4 md:h-5 md:w-5 text-orange-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <h4 className="text-xs md:text-sm font-medium text-orange-800 mb-1">
                    {language === 'ar' ? 'مكالمة معلقة' : 'Stale Call Detected'}
                  </h4>
                  <p className="text-xs md:text-sm text-orange-700 mb-2">
                    {language === 'ar' 
                      ? `توجد مكالمة قديمة (${Math.round(activeCallAgeMinutes || 0)} دقيقة). اضغط لمسحها.`
                      : `There's an old call (${Math.round(activeCallAgeMinutes || 0)} min old). Click to clear it.`
                    }
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      if (currentUser) {
                        await AvailabilityService.cancelActiveCall(currentUser.uid, waitingRoom?.activeCall?.roomId);
                        toast.success(language === 'ar' ? 'تم مسح المكالمة' : 'Call cleared');
                      }
                    }}
                    className="text-xs border-orange-300 hover:bg-orange-100"
                  >
                    {language === 'ar' ? 'مسح المكالمة المعلقة' : 'Clear Stale Call'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* No Approved Availability Warning */}
          {hasApprovedAvailability === false && (
            <div className="p-3 md:p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start gap-2 md:gap-3">
                <AlertCircle className="h-4 w-4 md:h-5 md:w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <h4 className="text-xs md:text-sm font-medium text-yellow-800 mb-1">
                    {language === 'ar' ? 'لا توجد جداول معتمدة لعيادتك' : 'No Approved Schedules for Your Clinic'}
                  </h4>
                  <p className="text-xs md:text-sm text-yellow-700">
                    {language === 'ar' 
                      ? 'يجب عليك تقديم جدول للاعتماد قبل أن تتمكن من استقبال المستفيدين.'
                      : 'You must submit a schedule for approval before you can receive customers.'
                    }
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Shareable Clinic Link */}
          <AnimatePresence>
            {showShareableLink && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="pt-4"
              >
                <p className="text-sm text-muted-foreground mb-3">
                  {language === 'ar' 
                    ? 'شارك هذا الرابط مع المستفيدين للانضمام لغرفة الإنتظار بدون موعد' 
                    : 'Share this link with customers to join the waiting room without an appointment'}
                </p>
                <div className="flex items-center gap-2 w-full">
                  <Input
                    value={getShareableLink()}
                    readOnly
                    className="text-xs md:text-sm bg-gray-50 flex-1"
                    dir="ltr"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCopyLink}
                    className="shrink-0"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

        </CardContent>
      </Card>



      {/* Waiting Room */}
      <Card>
        <CardHeader className="p-3 md:p-4 lg:p-6">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-1.5 md:gap-2 text-base md:text-lg">
              <Users className="h-4 w-4 md:h-5 md:w-5 flex-shrink-0" />
              <span>{language === 'ar' ? 'غرفة الإنتظار' : 'Waiting Room'}</span>
              {customersCount > 0 && (
                <Badge variant="secondary" className="text-xs">{customersCount}</Badge>
              )}
            </CardTitle>
            <div className="flex items-center gap-1.5">
              {waitingRoomAlerts.permission === 'default' && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => { void waitingRoomAlerts.requestPermission(); }}
                >
                  <Bell className="h-3.5 w-3.5 me-1" />
                  {language === 'ar' ? 'تفعيل التنبيهات' : 'Enable alerts'}
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => waitingRoomAlerts.setMuted(!waitingRoomAlerts.muted)}
                aria-pressed={waitingRoomAlerts.muted}
                aria-label={waitingRoomAlerts.muted
                  ? (language === 'ar' ? 'تشغيل صوت التنبيه' : 'Unmute alert sound')
                  : (language === 'ar' ? 'كتم صوت التنبيه' : 'Mute alert sound')}
                title={waitingRoomAlerts.muted
                  ? (language === 'ar' ? 'تشغيل صوت التنبيه' : 'Unmute alert sound')
                  : (language === 'ar' ? 'كتم صوت التنبيه' : 'Mute alert sound')}
              >
                {waitingRoomAlerts.muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="p-3 md:p-4 lg:p-6 pt-0">
          {customersCount === 0 ? (
            <div className="text-center py-6 md:py-8 text-muted-foreground">
              <Users className="h-10 w-10 md:h-12 md:w-12 mx-auto mb-3 md:mb-4 opacity-50" />
              <p className="text-sm md:text-base">{language === 'ar' ? 'لا يوجد مستفيدين في غرفة الإنتظار' : 'No customers in waiting room'}</p>
            </div>
          ) : (
            <div className="space-y-2 md:space-y-3">
              {presenceCustomers.map((customer: WaitingCustomer) => (
                <motion.div
                  key={customer.customerId}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-2.5 md:p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-2 md:gap-3 min-w-0">
                    <div className="w-7 h-7 md:w-8 md:h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <UserCheck className="h-4 w-4 text-primary" />
                    </div>
                    
                    <div className="min-w-0 flex-1">
                      <h4 className="font-medium text-sm md:text-base truncate">{customer.customerName}</h4>
                      <p className="text-xs md:text-sm text-muted-foreground">
                        {language === 'ar' ? 'ينتظر منذ' : 'Waiting for'} {
                          (() => {
                            try {
                              if (customer.joinTime?.toDate) {
                                return formatDistanceToNow(customer.joinTime.toDate());
                              } else if (customer.joinTime?.seconds) {
                                return formatDistanceToNow(new Date(customer.joinTime.seconds * 1000));
                              } else if (customer.joinTime) {
                                return formatDistanceToNow(new Date(customer.joinTime as unknown as string | number));
                              }
                              return '...';
                            } catch {
                              return '...';
                            }
                          })()
                        }
                      </p>
                    </div>
                  </div>
                  
                  <Button 
                    size="sm" 
                    onClick={() => handleStartCall(customer.customerId)}
                    disabled={hasActiveCall}
                    className="bg-[#004250] hover:bg-[#003240] text-white w-full sm:w-auto text-xs md:text-sm h-8 md:h-9 disabled:opacity-50"
                  >
                    <Video className="h-3 w-3 md:h-3.5 md:w-3.5 mr-1.5" />
                    {language === 'ar' ? 'ابدأ المكالمة' : 'Start the Call'}
                  </Button>
                </motion.div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Attended Customers */}
      {currentUser && (
        <AttendedCustomersCard 
          attendedCustomers={waitingRoom?.attendedCustomers || []} 
          coachId={currentUser.uid}
        />
      )}

      {/* Today's Stats - Only show if admin enabled it */}
      {availabilitySession && showStatistics && (
        <Card>
          <CardHeader className="p-3 md:p-4 lg:p-6">
            <CardTitle className="flex items-center gap-1.5 md:gap-2 text-base md:text-lg">
              <BarChart3 className="h-4 w-4 md:h-5 md:w-5 flex-shrink-0" />
              {language === 'ar' ? 'إحصائيات اليوم' : 'Today\'s Stats'}
            </CardTitle>
          </CardHeader>
          
          <CardContent className="p-3 md:p-4 lg:p-6 pt-0">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
              <div className="text-center p-2 md:p-3 bg-gray-50 rounded-lg">
                <div className="text-xl md:text-2xl font-bold text-primary">
                  {availabilitySession.stats.callsCompleted}
                </div>
                <p className="text-xs md:text-sm text-muted-foreground mt-1">
                  {language === 'ar' ? 'مكالمات مكتملة' : 'Calls Completed'}
                </p>
              </div>
              
              <div className="text-center p-2 md:p-3 bg-gray-50 rounded-lg">
                <div className="text-xl md:text-2xl font-bold text-primary">
                  {Math.round(availabilitySession.stats.totalCallDuration)}
                </div>
                <p className="text-xs md:text-sm text-muted-foreground mt-1">
                  {language === 'ar' ? 'دقائق إجمالية' : 'Total Minutes'}
                </p>
              </div>
              
              <div className="text-center p-2 md:p-3 bg-gray-50 rounded-lg">
                <div className="text-xl md:text-2xl font-bold text-primary">
                  {availabilitySession.stats.customersServed}
                </div>
                <p className="text-xs md:text-sm text-muted-foreground mt-1">
                  {language === 'ar' ? 'مستفيدين تم خدمتهم' : 'Customers Served'}
                </p>
              </div>
              
              <div className="text-center p-2 md:p-3 bg-gray-50 rounded-lg">
                <div className="text-xl md:text-2xl font-bold text-primary">
                  {Math.round(availabilitySession.stats.averageWaitTime)}
                </div>
                <p className="text-xs md:text-sm text-muted-foreground mt-1">
                  {language === 'ar' ? 'متوسط الانتظار' : 'Avg Wait (min)'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Video Call Component */}
      <FloatingVideoCall
        roomId={currentCallRoomId}
        userRole="coach"
        userName={userData?.firstNameEn || 'Coach'}
        otherParticipantName={currentCustomerName}
        onCallEnded={handleCallEnded}
        open={showVideoCall && !!currentCallRoomId}
        attempt={currentUser && currentCallCustomerId ? { coachId: currentUser.uid, customerId: currentCallCustomerId } : undefined}
        onRemoteJoined={handleRemoteJoined}
      />

      {/* Customer File Dialog */}
      {showCustomerFile && selectedCustomerForFile && (
        <NotesDialog
          appointment={selectedCustomerForFile}
          onOpenChange={setShowCustomerFile}
          onNotesUpdate={() => {}} // No specific action needed for waiting room customers
        />
      )}
    </div>
  );
}
